/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';

// Component imports
import { Button, Card, Col, Form, Popover, Row, Space, Tag, Typography } from 'antd';
import { FilterOutlined } from '@ant-design/icons';
import LiveChatDateFilter from '../LiveChat/Filters/LiveChatDateFilter';
import LiveChatAgentFilter from '../LiveChat/Filters/LiveChatAgentFilter';
import LiveChatBotsFilter from '../LiveChat/Filters/LiveChatBotsFilter';
import LiveChatStatusFilter from '../LiveChat/Filters/LiveChatStatusFilter';
import { ArrowDownIcon } from '../Icons/ArrowDownIcon';

// Misc imports
import { useFetchOrganizationQuery } from '../../store/organization/organizationApi';
import { useFetchUsersThinListQuery } from '../../store/users/usersApi';
import { ADDITIONAL_ROLES_ORG } from '../../constants/main';
import { useFetchBotsQuery } from '../../store/bots/botsApi';
import { isEmptyArray } from '../../utils/is/isEmptyArray';
import { useFetchConversationsCountQuery, useLazyExportConversationsQuery } from '../../store/liveChat/liveChatApi';
import { revertBlobTypes } from '../../store/utils/revertBlobTypes';
import { CONVERSATION_STATUSES } from '../../constants/liveChat';
import handleFileDownload from '../../utils/misc/handleFileDownload';

// Constant declarations
const { Text } = Typography;

const LimitedTags = ({ items, renderItemLabel, onRemoveItem }) => {
  const firstFiveItems = items.slice(0, 5);
  const remainingItems = items.slice(5);

  return (
    <Fragment>
      {
        firstFiveItems.map((item) => (
          <Tag
            key={item}
            className="border-0 bg-body border-radius-base py-1 px-3"
            closable
            onClose={() => onRemoveItem(item)}
          >
            {renderItemLabel(item)}
          </Tag>
        ))
      }

      {
        remainingItems.length > 0 && (
          <Popover
            content={
              <div style={{ maxHeight: 200 }} className="scrollbar-custom scrollbar-custom-nohide overflow-y-auto">
                <Space direction="vertical">
                  {
                    remainingItems.map((item) => (
                      <Tag
                        key={item}
                        className="border-0 bg-body border-radius-base py-1 px-3"
                        closable
                        onClose={() => onRemoveItem(item)}
                      >
                        {renderItemLabel(item)}
                      </Tag>
                    ))
                  }
                </Space>
              </div>
            }
            overlayClassName="py-5 pl-5 pr-0"
            placement="right"
          >
            <Tag className="border-0 bg-body border-radius-base cursor-pointer py-1 px-3">
              +{remainingItems.length}
            </Tag>
          </Popover>
        )
      }
    </Fragment>
  );
};

const DownloadLiveChatConversationsPanel = () => {
  const { orgId } = useParams();
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const { currentUser } = useSelector((state) => state.auth);
  const { data: organization } = useFetchOrganizationQuery({ orgId }, { skip: !orgId });

  const [initialValues] = useState({
    status: `${CONVERSATION_STATUSES.STARTED},${CONVERSATION_STATUSES.FINISHED}`,
    bots: [],
    users: [],
    date: '',
    customDate: [
      // Past 7 days
      moment.tz(new Date(), organization?.timezone).subtract(7, 'days').startOf('day'),
      moment.tz(new Date(), organization?.timezone).endOf('day')
    ]
  });

  const requestParams = {
    status: form.getFieldValue('status') || initialValues.status,
    ...(form.getFieldValue('bots') && !isEmptyArray(form.getFieldValue('bots')) && { tenantIds: form.getFieldValue('bots')?.join(',') }),
    ...(form.getFieldValue('users') && !isEmptyArray(form.getFieldValue('users')) && { userIds: form.getFieldValue('users')?.join(',') }),
    ...(form.getFieldValue('date') && { dateRange: `${encodeURIComponent(form.getFieldValue('date') === 'custom' ? form.getFieldValue('customDate')[0] : form.getFieldValue('date'))},${encodeURIComponent(form.getFieldValue('date') === 'custom' ? form.getFieldValue('customDate')[1] : moment.tz(new Date(), organization?.timezone).endOf('day').format())}` })
  };

  const { data: bots } = useFetchBotsQuery({ orgId }, { skip: !orgId });
  const { data: usersThinList } = useFetchUsersThinListQuery({ orgId, urlParams: { organizationRole: ADDITIONAL_ROLES_ORG.LIVE_AGENT } }, { skip: !orgId });
  const [exportConversations, { isLoading: isExportingConversations }] = useLazyExportConversationsQuery();
  const { data: conversationsCount } = useFetchConversationsCountQuery({ orgId, urlParams: requestParams }, { skip: !orgId });

  const [allConversationsCount, setAllConversationsCount] = useState(null);
  const [, updateState] = useState(); // Hook to force a component re-render

  useEffect(() => {
    if (conversationsCount && !allConversationsCount) {
      setAllConversationsCount(conversationsCount);
    }
  }, [conversationsCount]);

  const exportConversationsZip = async () => {
    const result = await exportConversations({ orgId, urlParams: requestParams }).unwrap();

    const file = revertBlobTypes(result, 'application/zip');

    if (file) {
      const contentDisposition = file?.headers['content-disposition'];
      let fileName = 'helvia-lc-conversations.zip';

      if (contentDisposition) {
        const matches = /filename="([^"]+)"/.exec(contentDisposition);
        if (matches && matches[1]) {
          fileName = matches[1];
        }
      }

      handleFileDownload(file?.blob, fileName);
    }
  };

  return (
    <Fragment>
      <Form
        form={form}
        initialValues={initialValues}
        onFieldsChange={(_, allFields) => {
          updateState(allFields);
        }}
      >
        <Card className="p-0">
          <Row className="px-6 pt-6 pb-7">
            <Col span={24} className="mb-2">
              <Typography.Text strong>{t('Download Transcripts')}</Typography.Text>
              <Typography.Paragraph>{t('Use the filters to select the conversation transcripts you want to download.')}</Typography.Paragraph>
            </Col>

            <Col span={24} className="mb-4">
              {t('Filter by')}:
            </Col>

            <Col span={24}>
              <Space direction="horizontal" size={12} wrap>
                <Popover
                  trigger="click"
                  showArrow={false}
                  placement="bottomLeft"
                  content={
                    <Form.Item
                      name="status"
                      noStyle
                    >
                      <LiveChatStatusFilter />
                    </Form.Item>
                  }
                >
                  <Button type="text" className="bordered border-color-text">
                    <Space size={6}>
                      <FilterOutlined className={form.getFieldValue('status') && form.getFieldValue('status') !== initialValues.status ? 'text-primary' : ''} />
                      {t('Status')}
                      <ArrowDownIcon />
                    </Space>
                  </Button>
                </Popover>

                <Popover
                  trigger="click"
                  showArrow={false}
                  placement="bottomLeft"
                  content={
                    <Form.Item
                      name="bots"
                      noStyle
                    >
                      <LiveChatBotsFilter
                        bots={bots}
                        form={form}
                      />
                    </Form.Item>
                  }
                >
                  <Button type="text" className="bordered border-color-text">
                    <Space size={6}>
                      <FilterOutlined className={form.getFieldValue('bots')?.length > 0 ? 'text-primary' : ''} />
                      {t('Agents')}
                      <ArrowDownIcon />
                    </Space>
                  </Button>
                </Popover>

                <Popover
                  trigger="click"
                  showArrow={false}
                  placement="bottomLeft"
                  content={
                    <Form.Item
                      name="users"
                      noStyle
                    >
                      <LiveChatAgentFilter
                        currentUser={currentUser}
                        usersThinList={usersThinList}
                        form={form}
                      />
                    </Form.Item>
                  }
                >
                  <Button type="text" className="bordered border-color-text">
                    <Space size={6}>
                      <FilterOutlined className={form.getFieldValue('users')?.length > 0 ? 'text-primary' : ''} />
                      {t('Agents', { context: 'livechat agents' })}
                      <ArrowDownIcon />
                    </Space>
                  </Button>
                </Popover>

                <Popover
                  trigger="click"
                  showArrow={false}
                  placement="bottomLeft"
                  content={
                    <Form.Item
                      name="date"
                      noStyle
                    >
                      <LiveChatDateFilter
                        organization={organization}
                        form={form}
                      />
                    </Form.Item>
                  }
                >
                  <Button type="text" className="bordered border-color-text">
                    <Space size={6}>
                      <FilterOutlined className={(form.getFieldValue('date') || form.getFieldValue('customDate')) && form.getFieldValue('date') !== '' ? 'text-primary' : ''} />
                      {t('Date')}
                      <ArrowDownIcon />
                    </Space>
                  </Button>
                </Popover>
              </Space>
            </Col>
          </Row>

          {
            JSON.stringify(form.getFieldsValue(true)) !== JSON.stringify(initialValues) ? (
              <Row className="px-6 pb-4">
                <Col span={24} className="mb-4">
                  {t('Applied filters')}:
                </Col>

                <Col span={24} className="mb-4">
                  <Space direction="vertical" size={18} className="w-100">
                    {
                      form.getFieldValue('status') && form.getFieldValue('status') !== initialValues.status ? (
                        <Row gutter={[16, 8]} align="middle" wrap={false}>
                          <Col flex="100px">
                            <Space direction="horizontal">
                              <FilterOutlined />
                              {t('Status')}:
                            </Space>
                          </Col>

                          <Col flex="auto">
                            {
                              form.getFieldValue('status').split(',').map((status, index) => (
                                <Tag
                                  key={`${status}-${index}-${Date.now()}`} // Ensure unique key
                                  className="border-0 bg-body border-radius-base py-1 px-3"
                                  closable
                                  onClose={() => {
                                    const updatedStatus = form
                                      .getFieldValue('status')
                                      .split(',')
                                      .filter(s => s !== status);

                                    form.setFieldsValue({
                                      status: updatedStatus.length > 0 ? updatedStatus.join(',') : initialValues.status
                                    });
                                    updateState(Date.now());
                                  }}
                                >
                                  {t(status === CONVERSATION_STATUSES.STARTED ? 'Open Conversations' : 'Closed Conversations')}
                                </Tag>
                              ))
                            }
                          </Col>
                        </Row>
                      ) : null
                    }

                    {
                      form.getFieldValue('bots')?.length > 0 ? (
                        <Row gutter={[16, 8]} align="middle" wrap={false}>
                          <Col flex="100px">
                            <Space direction="horizontal">
                              <FilterOutlined />
                              {t('Agents')}:
                            </Space>
                          </Col>

                          <Col flex="auto">
                            <Space direction="horizontal" wrap>
                              <LimitedTags
                                items={form.getFieldValue('bots')}
                                renderItemLabel={(botId) =>
                                  bots?.items.find((bot) => bot.id === botId)?.displayName
                                }
                                onRemoveItem={(botId) => {
                                  const updatedBots = form.getFieldValue('bots').filter((id) => id !== botId);
                                  form.setFieldsValue({ bots: updatedBots });
                                  updateState(Date.now());
                                }}
                              />
                            </Space>
                          </Col>
                        </Row>
                      ) : null
                    }

                    {
                      form.getFieldValue('users')?.length > 0 ? (
                        <Row gutter={[16, 8]} align="middle" wrap={false}>
                          <Col flex="100px">
                            <Space direction="horizontal">
                              <FilterOutlined />
                              {t('Agents', { context: 'livechat agents' })}:
                            </Space>
                          </Col>

                          <Col flex="auto">
                            <Space direction="horizontal" wrap>
                              <LimitedTags
                                items={form.getFieldValue('users')}
                                renderItemLabel={(userId) =>
                                  userId === currentUser.id
                                    ? 'Me'
                                    : usersThinList?.find((user) => user.id === userId)?.fullName
                                }
                                onRemoveItem={(userId) => {
                                  const updatedUsers = form.getFieldValue('users').filter((id) => id !== userId);
                                  form.setFieldsValue({ users: updatedUsers });
                                  updateState(Date.now());
                                }}
                              />
                            </Space>
                          </Col>
                        </Row>
                      ) : null
                    }

                    {
                      (form.getFieldValue('date') || form.getFieldValue('customDate')) && form.getFieldValue('date') !== '' ? (
                        <Row gutter={[16, 8]} align="middle" wrap={false}>
                          <Col flex="100px">
                            <Space direction="horizontal">
                              <FilterOutlined />
                              {t('Dates')}:
                            </Space>
                          </Col>

                          <Col flex="auto">
                            <Tag
                              className="border-0 bg-body border-radius-base py-1 px-3"
                              closable={form.getFieldValue('date') || form.getFieldValue('customDate')}
                              onClose={() => {
                                form.setFieldsValue({ date: '' });
                                form.setFieldsValue({ customDate: initialValues.customDate });
                                updateState(Date.now());
                              }}
                            >
                              {
                                // If 'date', it's a single date. If 'customDate', it's a date range
                                form.getFieldValue('date') && form.getFieldValue('date') !== 'custom' && form.getFieldValue('date') !== '' ? (
                                  <Fragment>
                                    {moment(form.getFieldValue('date')).format('DD MMM YYYY')}
                                  </Fragment>
                                ) : form.getFieldValue('customDate') ? (
                                  <Fragment>
                                    <Text type="secondary">{t('Custom date range')}:</Text> {moment(form.getFieldValue('customDate')[0]).format('DD MMM YYYY')} - {moment(form.getFieldValue('customDate')[1]).format('DD MMM YYYY')}
                                  </Fragment>
                                ) : null
                              }
                            </Tag>
                          </Col>
                        </Row>
                      ) : null
                    }
                  </Space>
                </Col>
              </Row>
            ) : null
          }

          <Row className="border-top p-4" justify="end">
            <Col>
              <Space direction="horizontal" size="large">
                <Text type="secondary">
                  {
                    allConversationsCount === conversationsCount ? (
                      t('All Selected: {{count}}', { count: conversationsCount })
                    ) : (
                      t('Selected: {{count}}', { count: conversationsCount })
                    )
                  }
                </Text>

                <Button
                  type="primary"
                  size="large"
                  disabled={!conversationsCount || Number(conversationsCount) === 0}
                  loading={isExportingConversations}
                  onClick={exportConversationsZip}
                >
                  {t('Download')}
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>
      </Form>
    </Fragment>
  );
};

LimitedTags.propTypes = {
  items: PropTypes.array,
  renderItemLabel: PropTypes.func,
  onRemoveItem: PropTypes.func
};

export default DownloadLiveChatConversationsPanel;
